import type { FC } from "react";
import type React from "react";

import type { RawParams } from "@uirouter/core";

import { i18nFilter } from "app/filters/i18nFilter";
import {
    Switcher,
    GraphHeaderContainer,
    SwitchContainer,
    TitleContainer,
    ButtonsContainer,
} from "app/pages/industry-analysis/category-share/CategoryShareGraph/CategoryShareGraphStyles";
import { MonthToDateToggle } from "app/pages/industry-analysis/category-share/CategoryShareGraph/components/MonthToDateToggle";
import {
    resolveDataGranulairtyFromSwitcher,
    getCategoryDetailsForDashboard,
} from "app/pages/industry-analysis/category-share/CategoryShareGraph/utils/CategoryShareGraphUtils";
import type { ICategoryShareServices } from "app/pages/industry-analysis/category-share/CategoryShareTypes";
import { useNoRenderA2D } from "app/pages/industry-analysis/frame/WithNoRender";
import { PngDownload } from "app/pages/website-analysis/traffic-sources/search/tabs/SearchOverviewGraph/Components/PngDownload";
import { AddToDashboard as AddToDashboardComponent } from "app/pages/website-analysis/TrafficAndEngagement/ChartUtilities/AddToDashboard";

import type { GranularitySwitcherEnum } from "../CategoryShareGraphTypes";
import { DataTypeSwitcherEnum } from "../CategoryShareGraphTypes";

interface ICategoryShareGraphHeaderProps {
    selectedDataType: DataTypeSwitcherEnum;
    onSelectDataType: (index: number) => void;
    selectedGranularity: GranularitySwitcherEnum;
    onSelectGranularity: (index: number) => void;
    onToggleMonthToDate: (isEnabled: boolean) => void;
    graphRef: React.MutableRefObject<HTMLDivElement>;
    services: ICategoryShareServices;
    params: RawParams;
    isMonthlyGranularitySupported: boolean;
    isMTDSupported: boolean;
    isMTDActive: boolean;
}

export const CategoryShareGraphHeader: FC<ICategoryShareGraphHeaderProps> = (props) => {
    const {
        selectedDataType,
        onSelectDataType,
        selectedGranularity,
        onSelectGranularity,
        onToggleMonthToDate,
        graphRef,
        services,
        params,
        isMTDSupported,
        isMTDActive,
        isMonthlyGranularitySupported,
    } = props;

    const i18n = i18nFilter();
    const { isA2DAvailable } = useNoRenderA2D();

    const categoryDetails = services.categoryService.categoryQueryParamToCategoryObject(params.category);

    const isDashboardDisabled = selectedDataType === DataTypeSwitcherEnum.NUMBER;

    return (
        <GraphHeaderContainer>
            <TitleContainer>{services.translate("categoryShare.graph.title")}</TitleContainer>
            <ButtonsContainer>
                <MonthToDateToggle
                    isMonthToDateActive={isMTDActive}
                    onToggleMonthToDate={onToggleMonthToDate}
                    services={services}
                    isDisabled={!isMTDSupported}
                />

                <SwitchContainer>
                    <Switcher
                        itemList={[{ title: "%" }, { title: "#" }]}
                        selectedIndex={selectedDataType}
                        onItemClick={onSelectDataType}
                    />
                </SwitchContainer>

                <SwitchContainer>
                    <Switcher
                        itemList={[
                            { title: "D" },
                            { title: "W" },
                            { title: "M", disabled: !isMonthlyGranularitySupported },
                        ]}
                        selectedIndex={selectedGranularity}
                        onItemClick={onSelectGranularity}
                    />
                </SwitchContainer>
                <PngDownload
                    chartRef={graphRef}
                    metricName={services.translate("keywords.research.common.traffic.chart.header")}
                    offset={{ x: 0, y: 100 }}
                />
                {isA2DAvailable ? (
                    <AddToDashboardComponent
                        isDisabled={isDashboardDisabled}
                        tooltipContent={
                            isDashboardDisabled
                                ? i18n("industry.research.trends.graph.add.to.dashboard.disabled")
                                : null
                        }
                        metric={{
                            addToDashboardName: "TopCategoryShare",
                            chartType: "CategoryShareGraphDashboard",
                            title: "1",
                        }}
                        webSource={params.webSource}
                        filters={{
                            timeGranularity: resolveDataGranulairtyFromSwitcher(selectedGranularity),
                            includeSubDomains: true,
                            keys: categoryDetails.forApi,
                        }}
                        overrideParams={{
                            key: [
                                {
                                    ...getCategoryDetailsForDashboard(params, services),
                                },
                            ],
                        }}
                    />
                ) : null}
            </ButtonsContainer>
        </GraphHeaderContainer>
    );
};
